import { HiExclamationCircle, HiX } from "react-icons/hi";
import { MdSave } from "react-icons/md";
import { Alert } from "react-bootstrap";
import { ROUTES } from "../../../routes";
import { useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FaLock, FaLockOpen } from "react-icons/fa";
import ModulePopup from "../../../modules/popup/popup";
import FGInput from "../../../components/dumb/FGInput";
import usePageTitle from "../../../hooks/use-page-title";
import ApiWLFormat from "../../../modules/api/wl-format";
import ApiProgramme from "../../../modules/api/programme";
import Notifier from "../../../modules/notifier/notifier";
import Debouncer from "../../../modules/debouncer/debouncer";
import FGTextarea from "../../../components/dumb/FGTextarea";
import FGDropdownApi from "../../../components/form/FGDropdownApi";
import { BsBoxArrowInDown, BsFillExclamationTriangleFill } from "react-icons/bs";
import Multilingual from "../../../modules/multilingual/multilingual";
import { PortalContext, usePortalSettingSet } from "../../../context/portal-context";
import WLFieldsEditor, {
  WLField,
} from "../../../components/dumb/WLFieldsEditor";
import FooterBoxAction from "../../../components/common/FooterBoxAction";

export default function PageWLFormatEdit() {
  const title = "Edit Workplace Learning Report";
  usePageTitle(title);
  usePortalSettingSet("label_header", title);
  const { setCustomText } = useContext(PortalContext);
  const { _id } = useParams();
  const [contentChanged, setContentChanged] = useState(false);
  const [sending, setSending] = useState(false);
  const [found, setFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    isArchived: false,
    fields: [],
    cohorts: [],
    programme: null,
  });

  useEffect(() => {
    setCustomText(title);
  }, []);

  useEffect(() => {
    ApiWLFormat.getById(_id)
      .then((res) => {
        setLoading(false);
        if (res.result === "OK") {
          setFound(true);
          setFormData(res.data);
        } else {
          Notifier.error(res.message);
        }
      })
      .catch((error) => {
        setLoading(false);
        Notifier.error(error.message);
      });
  }, []);

  const handleOnChange = (e: any) => {
    setContentChanged(true);
    setFormData((d) => ({
      ...d,
      [e.target.name]: e.target.value,
    }));
  };

  const handleOnChangeFields = (value: Array<WLField>) => {
    setContentChanged(true);
    setFormData((d) => ({ ...d, fields: value }));
  };

  const showAlertError = (text: string) => {
    ModulePopup.showAlert({
      title: <>
        <HiExclamationCircle />
        Alert
      </>,
      description: <>
        <p>{text}</p>
      </>
    })
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (!formData.fields.length) {
      showAlertError('"Sections" is empty. Please add one at least.');
      return;
    }
    if (!formData.programme) {
      showAlertError("Please choose a Programme.");
      return;
    }
    Debouncer.execute("UPDATE_DATA", async () => {
      setSending(true);
      ApiWLFormat.update(_id, formData)
        .then((res) => {
          setSending(false);
          if (res.result === "OK") {
            setFormData({
              ...formData,
              name: res.data.name,
              fields: res.data.fields,
              description: res.data.description,
              programme: res.data.programme,
            });
            setContentChanged(false);
            Notifier.success(res.message);
          } else {
            showAlertError(res.message);
          }
        })
        .catch((error) => {
          setSending(false);
          showAlertError(error.message);
        })
        .finally(() => setContentChanged(false));
    });
  };

  const updateState = () => {
    ModulePopup.showAlertConfirm({
      title: "Confirmation",
      description: (
        <p>
          Are you sure you want to {formData.isArchived ? "unarchive" : "archive"} this Workplace Learning Report?
        </p>
      ),
      onYes: () =>
        Debouncer.execute("UPDATE_STATE_WL_FORMAT", () => {
          ApiWLFormat.updateState(_id, !formData.isArchived)
            .then((res) => {
              if (res.result === "OK") {
                setFormData({
                  ...formData,
                  name: res.data.name,
                  fields: res.data.fields,
                  description: res.data.description,
                  isArchived: res.data.isArchived,
                  programme: res.data.programme,
                });
                Notifier.success(
                  "Your Workplace Learning Report has been updated."
                );
              } else {
                Notifier.error(res.message);
              }
            })
            .catch((error) => {
              Notifier.error(error.message);
            })
            .finally(() => setContentChanged(false));
        }),
    });
  };

  return (
    <main className="form-generic content-generic">
      {/* <h1 className="fs-title hide-mobile-down">{title}</h1> */}

      {/* <div className="cg__actions hide-mobile-down">
        <Link
          to={ROUTES.PORTAL_WL_FORMATS}
          className="btn btn-primary"
          onClick={(e) => {
            if (
              contentChanged &&
              !window.confirm(Multilingual.get("CONFIRM_ACTION_CANT_BE_UNDONE"))
            ) {
              e.preventDefault();
            }
          }}
        >
          <HiArrowLeft /> Back
        </Link>
      </div> */}

      {loading && <p className="alert alert-info">Loading data...</p>}

      {!loading && !found && (
        <p className="alert alert-info">Format not found.</p>
      )}

      {!loading && found && (
        <form onSubmit={(e) => {
          e.preventDefault();
          ModulePopup.showAlertConfirm({
            title: "Confirmation",
            description: "Are you sure you want to save the changes?",
            onYes() {
              handleSubmit(e);
            },
          });
        }} className="box-default bg-white p-3">
          {(formData.isArchived || formData.cohorts.length > 0) && (
            <>
              {formData.isArchived ? (
                <Alert variant="warning" className="d-flex">
                  <FaLock
                    size={20}
                    color="#FF991F"
                    style={{ marginRight: 10 }}
                  />
                  <div>
                    This Workplace Learning Report has been archived. You are not allowed to make any changes to the Report.
                  </div>
                </Alert>
              ) : (
                <Alert variant="warning" className="d-flex">
                  <BsFillExclamationTriangleFill
                    size={20}
                    color="#FF991F"
                    style={{ marginRight: 10 }}
                  />{" "}
                  <span>
                    This Report is already assigned to:{" "}
                    {formData.cohorts.map((c) => c.name).join(", ")} <br /><br/>
                    You are only allowed to change the following: Section Title
                    and its Description, Description of the Report
                  </span>
                </Alert>
              )}
            </>
          )}

          <FGInput
            name="name"
            id="name"
            type="text"
            label="Name of Report"
            required={!formData.isArchived}
            disabled={formData.isArchived || formData.cohorts.length > 0}
            autoFocus
            value={formData.name}
            onChange={handleOnChange}
          />

          <WLFieldsEditor
            value={formData.fields}
            archived={formData.isArchived}
            cohortCount={formData.cohorts.length}
            disabled={formData.isArchived || formData.cohorts.length > 0}
            onChange={handleOnChangeFields}
          />

          <FGTextarea
            name="description"
            id="description"
            label="Description of Report"
            value={formData.description}
            disabled={formData.isArchived}
            onChange={handleOnChange}
          />

          <FGDropdownApi
            required
            label="Choose a Programme"
            name="programme"
            defaultValue={formData.programme}
            api={ApiProgramme.getOptions}
            onChange={handleOnChange}
          />

          <div className="d-flex justify-content-between">
            <div>
              {!formData.isArchived && (
                <>
                  <button
                    type="submit"
                    className="btn btn-secondary"
                    disabled={sending}
                  >
                    <BsBoxArrowInDown className="icon-pos-upper" /> Save
                  </button>{" "}
                </>
              )}
              <Link
                to={ROUTES.PORTAL_WL_FORMATS}
                className="btn btn-primary"
                onClick={(e) => {
                  if (
                    contentChanged &&
                    !window.confirm(
                      Multilingual.get("CONFIRM_ACTION_CANT_BE_UNDONE")
                    )
                  ) {
                    e.preventDefault();
                  }
                }}
              >
                <HiX /> Cancel
              </Link>
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={updateState}
            >
              {formData.isArchived ? (
                <>
                  {" "}
                  <FaLockOpen /> Unarchive{" "}
                </>
              ) : (
                <>
                  {" "}
                  <FaLock /> Archive{" "}
                </>
              )}
            </button>
          </div>
        </form>
      )}

      <FooterBoxAction
        backUrl={ROUTES.PORTAL_WL_FORMATS}
        onBackUrlClick={(e) => {
          if (
            contentChanged &&
            !window.confirm(Multilingual.get("CONFIRM_ACTION_CANT_BE_UNDONE"))
          ) {
            e.preventDefault();
          }
        }}
      />
    </main>
  );
}
